// The sponsors of ONE event — the "Event Sponsors" section of its public page (ADR-0016). Sportya's
// own partners are the other model, administered under Parteneri, and never reach this tab.
//
// Everything here is the shape of one PUT /admin/events/:eventId/sponsors write. The write is the
// WHOLE list, never a delta: the editor owns the full picture, and a list replaced whole cannot
// disagree with itself about which rows exist. Ids are minted by the server — a row added here
// leaves without one and comes back on the socket with it.

import { normalizeWebsite } from "components/Common/SponsorBand/sponsorFields"

// Fixed in code, mirroring the API's EVENT_SPONSOR_TIERS (common/mongoDbSchema/schemaVariables):
// the model's enum and the gateway's Joi reject anything else, so this is the whole legal
// vocabulary. Order is display order. No "big plate" for the main partner — every plate is equal,
// and rank reads from the tier heading.
export const EVENT_SPONSOR_TIERS = [
  { key: "mainPartner", label: "Event Main Partner" },
  { key: "sponsor", label: "Event Sponsors" },
  { key: "mediaPartner", label: "Event Media Partners" },
]

// Where a logo lives in storage. POST /assets/events/sponsors answers with a bare filename; the
// stored `logoUrl` is the path under /assets, which the API deletes once no row refers to it.
export const EVENT_SPONSOR_LOGO_PATH = "events/sponsors"

/** `event.sponsors` as read, tolerant of an event that has none or a row that is not an object. */
export const eventSponsorRows = sponsors =>
  Array.isArray(sponsors) ? sponsors.filter(row => row && typeof row === "object") : []

// A row added in the editor needs a handle before the server has given it one: the band moves rows
// by id, and React keys them. The handle is local, recognisable, and stripped from the write.
const DRAFT_PREFIX = "draft:"
let drafts = 0
const draftId = () => `${DRAFT_PREFIX}${++drafts}`

export const isDraft = row => String(row?.id || "").startsWith(DRAFT_PREFIX)

export const sponsorLogoUrl = filename =>
  filename ? `${EVENT_SPONSOR_LOGO_PATH}/${String(filename).trim()}` : ""

/**
 * The form's fields for a sponsor — an existing one to edit, or nothing to add a fresh one in the
 * tier whose "+" was clicked. The opposite half of `sponsorFromForm`, with a round-trip test: a
 * field added to one without the other is how an edit quietly drops a value.
 */
export const sponsorFormValues = (sponsor = null, defaultTier = EVENT_SPONSOR_TIERS[0].key) => ({
  id: sponsor?.id || "",
  category: sponsor?.category || defaultTier,
  name: sponsor?.name || "",
  website: sponsor?.url || "",
  logoUrl: sponsor?.logoUrl || "",
  // Only ever set by a fresh upload: an edit that does not touch the logo keeps `logoUrl`.
  logoFilename: "",
})

/**
 * The row a saved form means, at the given order. An existing sponsor keeps its id; a new one gets
 * a draft handle, which `sponsorsWritePayload` strips so the server mints the real one.
 */
export const sponsorFromForm = (
  { id, category, name, logoFilename, logoUrl, website } = {},
  order = 0
) => ({
  id: id || draftId(),
  category,
  name: String(name ?? "").trim(),
  // An edit that did not re-upload keeps the stored path; a new upload wins.
  logoUrl: logoFilename ? sponsorLogoUrl(logoFilename) : String(logoUrl ?? "").trim(),
  url: normalizeWebsite(website),
  order: Number.isFinite(Number(order)) ? Number(order) : 0,
})

/**
 * The `sponsors` of one write: every row with exactly the keys the gateway's schema knows — an
 * unknown key is a 400 — and without an id for a draft. An empty website is left out rather than
 * sent as '', which is what the API stores for "no website" anyway.
 */
export const sponsorsWritePayload = rows =>
  eventSponsorRows(rows).map(({ id, category, name, logoUrl, url, order }) => ({
    ...(id && !isDraft({ id }) ? { id } : {}),
    category,
    name,
    logoUrl,
    ...(url ? { url } : {}),
    order: Number.isFinite(Number(order)) ? Number(order) : 0,
  }))

// The form asks for both a logo and a website (the decision of 2026-08-19), even though the API only
// insists on the logo; and the website has to come out http(s), which is all the gateway accepts —
// a bare hostname is fine (the scheme is added), `ftp:` or `javascript:` is marked here rather
// than bounced by a 400. Returns the field keys that are wrong, so the form can mark them.
export const sponsorFormErrors = ({ category, name, logoFilename, logoUrl, website } = {}) => {
  const errors = []
  if (!EVENT_SPONSOR_TIERS.some(({ key }) => key === category)) errors.push("tier")
  if (!String(name ?? "").trim()) errors.push("name")
  if (!logoFilename && !String(logoUrl ?? "").trim()) errors.push("logo")
  if (!/^https?:\/\//i.test(normalizeWebsite(website))) errors.push("website")
  return errors
}
